import type React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Grid,
  Typography,
  useTheme,
  CircularProgress,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  Alert,
} from "@mui/material";
import {
  Info as InfoIcon,
  ElectricBolt as PowerIcon,
  Wifi as InternetIcon,
  Speaker as PASystemIcon,
  People as ContactsIcon,
  CalendarMonth as BookingsIcon,
  Edit as EditIcon,
  AddCircleOutlineOutlined,
  Search as SearchIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import InfoCard from "./components/InfoCard";
import InfoRow from "./components/InfoRow";
import ContactCard from "./components/ContactCard";
import ViewVenuePhotos from "./tabs/ViewVenuePhotos";
import apiService from "../../service/Api";
import banquet from "../../assets/venue-layouts/banquet.png";
import theatre from "../../assets/venue-layouts/theatre.png";
import cocktail from "../../assets/venue-layouts/cocktail.jpg";
import classroom from "../../assets/venue-layouts/classroom.png";
import type { BookingResponse } from "../bookings/types/view-booking";
import type { PaginatedResponse } from "../contacts/components/types/view-contact";
import { tokens } from "../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import formatDate from "../../utils/FormatDate";
import type { IContact } from "./components/ContactAutoComplete";
import CustomTextField from "../../components/common/Textfield";
import { MuiTelInput } from "mui-tel-input";
import PhoneNumberField from "../../components/common/PhoneNumberField";
import ContactDetailsDialog from "./components/ContactDetailsDialog";

export interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue_id: number;
}

interface VenueContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  twitter_handle: string | null;
  instagram_handle: string | null;
  facebook_handle: string | null;
  created_at: string;
  updated_at: string;
  venue: number;
}

export interface VenueLayout {
  id: number;
  layout_type: string;
  capacity: string;
  created_at: string;
  updated_at: string;
  venue: number;
}

export interface VenueData {
  id: number;
  name: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  location: string | null;
  capacity: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  time_zone: string;
  country: string;
  has_power: boolean;
  power_type: string;
  power_outage_rate: string;
  power_socket_type: string;
  power_backup: string;
  backup_power_capacity: string;
  power_distance_from_livestream_desk: string | null;
  internet_service_provider: string | null;
  wifi_name: string;
  wifi_password: string;
  internet_upload_speed: number | null;
  router_accessibility: string;
  router_distance_from_livestream: string | null;
  has_pa_system: boolean;
  pa_system_position: string | null;
  pa_system_distance_from_livestream: string | null;
  pa_system_provider: string | null;
  other_pa_system_service_provider: string | null;
  other_pa_system_providers: string | null;
  pa_system_contact_name: string | null;
  pa_system_contact_phone: string | null;
  pa_system_contact_email: string | null;
  number_of_cameras: string | null;
  camera_positions: string | null;
  distance_of_each_camera_from_livestream_desk: string | null;
  floor_plan_image_urls: string | null;
  media_urls: string | null;
  venue_type: string;
  notes: string | null;
  travel_duration: string | null;
  show_other_input: string | null;
  created_at: string;
  updated_at: string;
  layouts: VenueLayout[];
  contacts: VenueContact[];
  files: VenueDocument[];

  created_by?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;

  last_updated_by?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface ViewVenueProps {
  venueData?: VenueData;
}

const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 500;

const ViewVenue: React.FC<ViewVenueProps> = ({
  venueData: defaultVenueData,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [venueData, setVenueData] = useState<VenueData | null>(
    defaultVenueData || null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();
  const [allBookings, setAllBookings] = useState<BookingResponse[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<BookingResponse[]>(
    []
  );

  const [hoverContact, setHoverContact] = useState<any | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const [openContactModal, setOpenContactModal] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [searchResults, setSearchResults] = useState<IContact[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [contactModalStep, setContactModalStep] = useState<"search" | "form">(
    "search"
  );

  const [searchAttempted, setSearchAttempted] = useState(false);

  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [newContactForm, setNewContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const isHoveringContactOrDialog = useRef(false);
  const contactHoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleContactHover = (contact: any) => {
    isHoveringContactOrDialog.current = true;

    if (contactHoverTimerRef.current) {
      clearTimeout(contactHoverTimerRef.current);
    }

    if (!hoverContact?.isEditing) {
      contactHoverTimerRef.current = setTimeout(() => {
        setHoverContact(contact);
        setIsContactDialogOpen(true);
      }, 800); // 800ms delay
    }
  };

  const handleContactLeave = () => {
    isHoveringContactOrDialog.current = false;

    if (contactHoverTimerRef.current) {
      clearTimeout(contactHoverTimerRef.current);
      contactHoverTimerRef.current = null;
    }

    if (isContactDialogOpen) {
      setTimeout(() => {
        if (!isHoveringContactOrDialog.current) {
          setIsContactDialogOpen(false);
        }
      }, 400);
    }
  };

  const handleContactDialogClose = () => {
    setIsContactDialogOpen(false);

    if (contactHoverTimerRef.current) {
      clearTimeout(contactHoverTimerRef.current);
    }
  };

  const searchContactsAPI = async (term: string) => {
    // Only make request if we have at least 3 characters
    if (term.trim().length < SEARCH_MIN_CHARS) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const res = await apiService.get<{ results: IContact[] }>(
        `/api/contacts/contact/?contact_type=individual&search=${encodeURIComponent(
          term.trim()
        )}`
      );
      setSearchResults(res.data.results || []);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search contacts");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setSearchAttempted(contactSearch.length > 0);

    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // Only set a new timer if we have input
    if (contactSearch.trim().length > 0) {
      searchTimerRef.current = setTimeout(() => {
        searchContactsAPI(contactSearch);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [contactSearch]);

  const handleAddExistingContact = async (contact: IContact) => {
    setSavingContact(true);
    try {
      const existingContactIds = Array.isArray(venueData?.contacts)
        ? venueData.contacts.map((c) => c.id)
        : [];

      await apiService.patch(`/api/venues/venue/${id}/add-contacts/`, {
        contact_ids: [...existingContactIds, contact.id],
      });

      if (venueData) {
        console.log(
          "Contact ID:",
          contact.id,
          "Venue ID:",
          id,
          "Name:",
          contact.first_name,
          contact.last_name
        );
        setVenueData({
          ...venueData,
          contacts: [
            ...venueData.contacts,
            {
              id: Number(contact.id),
              first_name: contact.first_name || "",
              last_name: contact.last_name || "",
              email: contact.email || "",
              phone: contact.phone || "",
              twitter_handle: null,
              instagram_handle: null,
              facebook_handle: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              venue: Number.parseInt(id || "0"),
            },
          ],
        });
      }

      toast.success("Contact added to venue successfully!");
      resetContactModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to add contact to venue";
      toast.error(errorMessage);
    } finally {
      setSavingContact(false);
    }
  };

  const handleAddNewContact = async () => {
    if (
      !newContactForm.first_name.trim() ||
      !newContactForm.last_name.trim() ||
      !newContactForm.email.trim()
    ) {
      toast.error("Please fill in First Name, Last Name, and Email");
      return;
    }

    setSavingContact(true);
    try {
      const contactRes = await apiService.post<any>(
        "/api/contacts/contact/",
        newContactForm
      );
      const newContact = contactRes.data;
      toast.success("Contact created successfully!");

      const existingContactIds = Array.isArray(venueData?.contacts)
        ? venueData.contacts.map((c) => c.id)
        : [];

      await apiService.patch(`/api/venues/venue/${id}/add-contacts/`, {
        contact_ids: [...existingContactIds, newContact.id],
      });

      toast.success("Contact added to venue successfully!");

      if (venueData) {
        console.log(
          "Contact ID:",
          newContact.id,
          "Venue ID:",
          id,
          "Name:",
          newContact.first_name,
          newContact.last_name
        );
        setVenueData({
          ...venueData,
          contacts: [
            ...venueData.contacts,
            {
              id: newContact.id,
              first_name: newContact.first_name,
              last_name: newContact.last_name,
              email: newContact.email || "",
              phone: newContact.phone || "",
              twitter_handle: null,
              instagram_handle: null,
              facebook_handle: null,
              created_at: newContact.created_at || new Date().toISOString(),
              updated_at: newContact.updated_at || new Date().toISOString(),
              venue: Number.parseInt(id || "0"),
            },
          ],
        });
      }

      resetContactModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to create contact";
      toast.error(errorMessage);
    } finally {
      setSavingContact(false);
    }
  };

  const resetContactModal = () => {
    setOpenContactModal(false);
    setContactModalStep("search");
    setContactSearch("");
    setSearchResults([]);
    setSelectedContact(null);
    setSearchAttempted(false);
    setNewContactForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [contactPage, setContactPage] = useState(0);
  const [contactRowsPerPage, setContactRowsPerPage] = useState(5);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeContactPage = (event: unknown, newPage: number) => {
    setContactPage(newPage);
  };

  const handleChangeContactRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setContactRowsPerPage(Number.parseInt(event.target.value, 10));
    setContactPage(0);
  };

  const paginatedContacts = (venueData?.contacts || []).slice(
    contactPage * contactRowsPerPage,
    contactPage * contactRowsPerPage + contactRowsPerPage
  );

  const paginatedBookings = filteredBookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const document_types = [
    "venue_photos",
    "pa_system",
    "equipment",
    "floor_plan",
  ];

  const formatTitle = (type: string) =>
    type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const layoutImages: Record<string, string> = {
    banquet: banquet,
    theater: theatre,
    classroom: classroom,
    cocktail: cocktail,
  };

  useEffect(() => {
    const fetchVenue = async () => {
      if (defaultVenueData) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get<VenueData>(
          `/api/venues/venue/${id}/`
        );

        const transformedData = {
          ...response.data,
          files: response.data.files.map((file: any) => ({
            ...file,
            venue_id: file.venue,
          })),
        };
        setVenueData(transformedData);
        toast.success("Venue data loaded successfully!", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      } catch (err: any) {
        let errorMessage = "Failed to fetch venue data. Please try again.";
        if (err.response?.status === 404) {
          errorMessage = "Venue not found.";
          navigate("/venues");
        } else if (err.response?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error("Fetch venue error:", err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [id, navigate, defaultVenueData]);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await apiService.get<
          PaginatedResponse<BookingResponse>
        >(`/api/bookings/booking/?venue_id=${id}`);

        setAllBookings(response.data.results);
        setFilteredBookings(response.data.results);
      } catch (err: any) {
        toast.error("Failed to load bookings");
      }
    };

    if (id) fetchBookings();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !venueData) {
    return (
      <Box textAlign="center" my={4}>
        <Typography color="error" variant="h6">
          {error || "No venue data available."}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/venues")}
          sx={{ mt: 2 }}
        >
          Back to Venues
        </Button>
      </Box>
    );
  }

  const tabContent = [
    <Box key="venue-details">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <InfoCard
            title="Basic Information"
            icon={<InfoIcon />}
            sx={{ height: "100%" }}
          >
            <Grid container spacing={2}>
              <InfoRow label="Venue Name" value={venueData.name || "N/A"} />
              <InfoRow
                label="Venue Type"
                value={venueData.venue_type || "N/A"}
              />
              <InfoRow
                label="Venue Capacity"
                value={venueData.capacity || "N/A"}
              />

              {venueData.layouts.map((layout, index) => (
                <InfoRow
                  key={layout.id}
                  label={`Layout ${index + 1} Capacity`}
                  value={`${layout.layout_type} - ${layout.capacity}`}
                />
              ))}

              <InfoRow
                label="Address"
                value={`${venueData.address_one}, ${venueData.address_two}, ${venueData.suburb}, ${venueData.city}, ${venueData.country}`}
              />
              <InfoRow label="Time Zone" value={venueData.time_zone || "N/A"} />
            </Grid>
          </InfoCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <InfoCard title="Power" icon={<PowerIcon />} sx={{ height: "100%" }}>
            <Grid container spacing={2}>
              <InfoRow
                label="Has Power"
                value={venueData.has_power ? "Yes" : "No"}
              />
              <InfoRow
                label="Power Type"
                value={venueData.power_type || "N/A"}
              />
              <InfoRow
                label="Power Outage Rate"
                value={venueData.power_outage_rate || "N/A"}
              />
              <InfoRow
                label="Power Socket Type"
                value={venueData.power_socket_type || "N/A"}
              />
              <InfoRow
                label="Power Backup"
                value={venueData.power_backup || "N/A"}
              />
              <InfoRow
                label="Backup Power Capacity"
                value={venueData.backup_power_capacity || "N/A"}
              />
              <InfoRow
                label="Distance from Livestream Desk"
                value={venueData.power_distance_from_livestream_desk || "N/A"}
              />
            </Grid>
          </InfoCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <InfoCard
            title="Internet"
            icon={<InternetIcon />}
            sx={{ height: "100%" }}
          >
            <Grid container spacing={2}>
              <InfoRow
                label="Internet Service Provider"
                value={venueData.internet_service_provider || "N/A"}
              />
              <InfoRow label="WiFi Name" value={venueData.wifi_name || "N/A"} />
              <InfoRow
                label="WiFi Password"
                value={venueData.wifi_password || "N/A"}
              />
              <InfoRow
                label="Upload Speed"
                value={
                  venueData.internet_upload_speed
                    ? `${venueData.internet_upload_speed} Mbps`
                    : "N/A"
                }
              />
              <InfoRow
                label="Router Accessibility"
                value={venueData.router_accessibility || "N/A"}
              />
              <InfoRow
                label="Router Distance"
                value={venueData.router_distance_from_livestream || "N/A"}
              />
            </Grid>
          </InfoCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <InfoCard
            title="P.A System"
            icon={<PASystemIcon />}
            sx={{ height: "100%" }}
          >
            <Grid container spacing={2}>
              <InfoRow
                label="Has P.A System"
                value={venueData.has_pa_system ? "Yes" : "No"}
              />
              <InfoRow
                label="Distance from Livestream Desk"
                value={venueData.pa_system_distance_from_livestream || "N/A"}
              />
              <InfoRow
                label="P.A System Provider"
                value={venueData.pa_system_provider || "N/A"}
              />
              <InfoRow
                label="Other P.A System Provider"
                value={venueData.other_pa_system_service_provider || "N/A"}
              />
              <InfoRow
                label="Contact Phone"
                value={venueData.pa_system_contact_phone || "N/A"}
              />
              <InfoRow
                label="Contact Email"
                value={venueData.pa_system_contact_email || "N/A"}
              />
            </Grid>
          </InfoCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <InfoCard
            title="Contacts"
            sx={{ height: "100%" }}
            icon={<ContactsIcon />}
            actionIcon={<AddCircleOutlineOutlined />}
            onActionClick={() => setOpenContactModal(true)}
          >
            {venueData.contacts.length > 0 ? (
              <>
                {paginatedContacts.map((contact, index) => (
                  <Box key={contact.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Venue Contact{" "}
                      {contactPage * contactRowsPerPage + index + 1}
                    </Typography>
                    <Box
                      onMouseEnter={() => handleContactHover(contact)}
                      onMouseLeave={handleContactLeave}
                      sx={{ cursor: "pointer" }}
                    >
                      <ContactCard
                        name={`${contact.first_name} ${contact.last_name}`}
                        phone={contact.phone || "N/A"}
                        email={contact.email || "N/A"}
                      />
                    </Box>
                  </Box>
                ))}

                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <TablePagination
                    component="div"
                    count={venueData.contacts.length}
                    page={contactPage}
                    onPageChange={handleChangeContactPage}
                    rowsPerPage={contactRowsPerPage}
                    onRowsPerPageChange={handleChangeContactRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{
                      "& .MuiToolbar-root": {
                        minHeight: "auto",
                        py: 1,
                      },
                    }}
                  />
                </Box>
              </>
            ) : (
              <Typography variant="subtitle2" gutterBottom>
                No Contacts Available
              </Typography>
            )}
          </InfoCard>
        </Grid>
        <ContactDetailsDialog
          open={isContactDialogOpen}
          contact={hoverContact}
          onClose={handleContactDialogClose}
          onHoverChange={(hovering, isEditing) => {
            if (isEditing) return; // DO NOT close modal when editing

            isHoveringContactOrDialog.current = hovering;

            if (!hovering) {
              setTimeout(() => {
                if (!isHoveringContactOrDialog.current) {
                  setIsContactDialogOpen(false);
                }
              }, 400);
            }
          }}
          onEdit={(updatedContact: any) => {
            if (venueData) {
              setVenueData({
                ...venueData,
                contacts: venueData.contacts.map((c) =>
                  c.id === updatedContact.id ? updatedContact : c
                ),
              });
            }
          }}
        />

        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <InfoCard
            title="Previous Bookings"
            sx={{ height: "100%" }}
            icon={<BookingsIcon />}
          >
            {filteredBookings?.length === 0 ? (
              <Typography
                variant="h4"
                sx={{
                  fontStyle: "italic",
                  textAlign: "center",
                  py: 4,
                  fontFamily: '"Freehand", cursive',
                }}
              >
                No Related Bookings
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Package</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedBookings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography
                              variant="h6"
                              sx={{
                                fontStyle: "italic",
                                fontFamily: '"Freehand", cursive',
                                py: 4,
                              }}
                            >
                              No Related Bookings
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedBookings.map((booking, index) => (
                          <TableRow
                            key={booking.id}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "white" : colors.primary[800],
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: colors.grey[500], // change to desired hover color
                              },
                            }}
                            onClick={() => navigate(`/bookings/${booking.id}`)}
                            style={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  src={
                                    booking?.deceased?.[0]?.person_image || ""
                                  }
                                  sx={{ width: 32, height: 32 }}
                                >
                                  {(
                                    booking?.deceased?.[0]?.first_name?.[0] ||
                                    ""
                                  ).toUpperCase()}
                                </Avatar>

                                <Typography variant="body2">
                                  {booking?.deceased?.[0]?.first_name &&
                                  booking?.deceased?.[0]?.surname
                                    ? `${booking.deceased[0].first_name} ${booking.deceased[0].surname}`
                                    : booking?.title ?? ""}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{booking.service}</TableCell>
                            <TableCell>{booking.package_item?.name}</TableCell>
                            <TableCell>
                              {formatDate(
                                booking.services?.[0]?.date_of_service
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredBookings.length > rowsPerPage && (
                  <TablePagination
                    component="div"
                    count={filteredBookings.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                )}
              </>
            )}
          </InfoCard>
        </Grid>
      </Grid>
    </Box>,
    ...document_types.map((docType) => (
      <Box key={docType}>
        <ViewVenuePhotos files={venueData.files} document_type={docType} />
      </Box>
    )),
    <Box key="layouts-container" sx={{ flexGrow: 1, mt: 2 }}>
      <Grid container spacing={2}>
        {venueData.layouts.map((layout, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            {layout.layout_type && layoutImages[layout.layout_type] && (
              <Paper
                sx={{
                  borderRadius: 2,
                  padding: 1,
                  textAlign: "center",
                  boxShadow: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  {layout.layout_type.charAt(0).toUpperCase() +
                    layout.layout_type.slice(1)}{" "}
                  Layout
                </Typography>
                <img
                  src={layoutImages[layout.layout_type] || "/placeholder.svg"}
                  alt={`${layout.layout_type} layout`}
                  style={{
                    width: "100%",
                    maxHeight: 150,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              </Paper>
            )}
          </Grid>
        ))}
      </Grid>
    </Box>,
  ];

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "primary.main",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "1rem",
              "&.Mui-selected": {
                color: "primary.main",
              },
            },
          }}
        >
          <Tab label="Venue Details" />
          {document_types.map((docType) => (
            <Tab key={docType} label={formatTitle(docType)} />
          ))}
          <Tab label="Venue Layout" />
        </Tabs>

        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          sx={{
            border: `2px solid ${colors.primary[100]} `,
            color: colors.primary[100],
          }}
          onClick={() => {
            navigate(`/venues/view/${id}/edit`);
          }}
        >
          Edit
        </Button>
      </Box>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {tabContent[activeTab]}
        </motion.div>
      </AnimatePresence>

      {(venueData.created_by || venueData.last_updated_by) && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: "100%",
            padding: "16px 24px",
            marginTop: "20px",
            backgroundColor: reduxTheme.primary_colors?.[900] || "#f8f8f8",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            borderRadius: "8px",
          }}
        >
          {venueData.created_by && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color={reduxTheme.neutral_colors?.[100]}
                sx={{ fontWeight: "medium" }}
              >
                Created By
              </Typography>

              <Tooltip
                title={venueData.created_by?.email || ""}
                placement="top"
              >
                <Typography
                  variant="body2"
                  color={reduxTheme.primary_colors?.[400] || "#fff"}
                  sx={{ fontWeight: "bold" }}
                >
                  {venueData?.created_by?.first_name}{" "}
                  {venueData?.created_by?.last_name}
                </Typography>
              </Tooltip>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color={reduxTheme.neutral_colors?.[100]}
              sx={{ fontWeight: "medium" }}
            >
              Last Updated By
            </Typography>

            {venueData.last_updated_by &&
            typeof venueData.last_updated_by !== "string" ? (
              <Tooltip
                title={venueData.last_updated_by?.email || ""}
                placement="top"
              >
                <Typography
                  variant="body2"
                  color={reduxTheme.primary_colors?.[400]}
                  sx={{ fontWeight: "bold" }}
                >
                  {venueData.last_updated_by?.first_name}{" "}
                  {venueData.last_updated_by?.last_name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                color={reduxTheme.primary_colors?.[400]}
                sx={{ fontWeight: "bold" }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Dialog
        open={openContactModal}
        onClose={resetContactModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {contactModalStep === "search"
            ? "Add Contact to Venue"
            : "Create New Contact"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {contactModalStep === "search" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <CustomTextField
                fullWidth
                placeholder="Search existing contacts..."
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                disabled={isSearching}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                }}
                variant="outlined"
              />

              {contactSearch.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {contactSearch.length < SEARCH_MIN_CHARS
                    ? `Type ${
                        SEARCH_MIN_CHARS - contactSearch.length
                      } more character${
                        SEARCH_MIN_CHARS - contactSearch.length > 1 ? "s" : ""
                      } to search`
                    : "Searching..."}
                </Typography>
              )}

              {/* Loading state */}
              {isSearching && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              )}

              {!isSearching &&
                searchAttempted &&
                contactSearch.trim().length >= SEARCH_MIN_CHARS && (
                  <>
                    {searchResults.length > 0 ? (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Found {searchResults.length} contact
                          {searchResults.length !== 1 ? "s" : ""}
                        </Typography>
                        <List sx={{ maxHeight: 300, overflow: "auto" }}>
                          {searchResults.map((contact) => (
                            <ListItemButton
                              key={contact.id}
                              onClick={() => handleAddExistingContact(contact)}
                              disabled={savingContact}
                              sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                mb: 1,
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {contact.first_name} {contact.last_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {contact.email}
                                </Typography>
                              </Box>
                            </ListItemButton>
                          ))}
                        </List>
                      </Box>
                    ) : (
                      <Alert severity="info">
                        No contacts found matching "{contactSearch}". Would you
                        like to{" "}
                        <Button
                          size="small"
                          onClick={() => setContactModalStep("form")}
                          sx={{ textTransform: "none" }}
                        >
                          create a new contact?
                        </Button>
                      </Alert>
                    )}
                  </>
                )}

              {/* Initial state - show helper message */}
              {!searchAttempted && (
                <Alert severity="info">
                  Type at least {SEARCH_MIN_CHARS} characters to search for
                  existing contacts
                </Alert>
              )}
            </Box>
          ) : (
            /* New Contact Form */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <CustomTextField
                fullWidth
                label="First Name"
                value={newContactForm.first_name}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    first_name: e.target.value,
                  })
                }
                disabled={savingContact}
              />
              <CustomTextField
                fullWidth
                label="Last Name"
                value={newContactForm.last_name}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    last_name: e.target.value,
                  })
                }
                disabled={savingContact}
              />
              <CustomTextField
                fullWidth
                label="Email"
                type="email"
                value={newContactForm.email}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    email: e.target.value,
                  })
                }
                disabled={savingContact}
              />

              <PhoneNumberField
                fullWidth
                defaultCountry="ZW"
                label="Phone Number"
                disabled={savingContact}
                value={newContactForm.phone ?? ""}
                onChange={(newPhoneNumber) =>
                  setNewContactForm({
                    ...newContactForm,
                    phone: newPhoneNumber,
                  })
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {contactModalStep === "form" && (
            <Button onClick={() => setContactModalStep("search")}>Back</Button>
          )}
          <Button onClick={resetContactModal} disabled={savingContact}>
            Cancel
          </Button>
          {contactModalStep === "form" && (
            <Button
              onClick={handleAddNewContact}
              variant="contained"
              disabled={
                !newContactForm.first_name.trim() ||
                !newContactForm.last_name.trim() ||
                !newContactForm.email.trim() ||
                savingContact
              }
            >
              {savingContact ? <CircularProgress size={20} /> : "Create & Add"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewVenue;
